import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  People as PeopleIcon,
  Store as StoreIcon,
  Event as EventIcon,
  QrCode as QrCodeIcon,
  // Download as DownloadIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  Contacts as ContactsIcon,
  Hub as NetworkIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import {
  DashboardMetricsResponse,
  CHART_COLORS,
} from '../types/dashboard.types';
import apiService from '../services/api';
import {
  MetricCard,
  UserMetricsSection,
  ExhibitorMetricsSection,
  MeetingsMetricsSection,
  LeadsMetricsSection,
  NetworkingMetricsSection,
  QrBadgesMetricsSection,
  SupportMetricsSection,
  FavoritesMetricsSection,
} from '../components/dashboard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      sx={{ pt: 3 }}
    >
      {value === index && children}
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);
  const hasFetched = useRef(false);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getAllMetrics();
      setMetrics(data);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to load dashboard metrics'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double fetch in React StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchMetrics();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!metrics) return null;

    const totalMeetings = metrics.meetings.meetingsByStatus.reduce(
      (sum, item) => sum + item.count,
      0
    );

    const totalConnections = metrics.networking.connectionsByStatus.reduce(
      (sum, item) => sum + item.count,
      0
    );

    const totalLeads = metrics.leads.leadsByTag.reduce(
      (sum, item) => sum + item.count,
      0
    );

    const totalQueries = metrics.support.queriesByType.reduce(
      (sum, item) => sum + item.count,
      0
    );

    return {
      activeUsers: metrics.users.activeUsers,
      inactiveUsers: metrics.users.inactiveUsers,
      newUsersThisWeek: metrics.users.newUsersThisWeek,
      newUsersThisMonth: metrics.users.newUsersThisMonth,
      totalExhibitors:
        metrics.exhibitors.verifiedExhibitors +
        metrics.exhibitors.unverifiedExhibitors,
      verifiedExhibitors: metrics.exhibitors.verifiedExhibitors,
      unverifiedExhibitors: metrics.exhibitors.unverifiedExhibitors,
      featuredExhibitors: metrics.exhibitors.featuredExhibitors,
      totalMeetings,
      completionRate: metrics.meetings.completionRate,
      noShowRate: metrics.meetings.noShowRate,
      activeBadges: metrics.qrBadges.activeBadges,
      totalQrScans: metrics.qrBadges.totalQrScans,
      totalConnections,
      totalConversations: metrics.networking.totalConversations,
      totalMessages: metrics.networking.totalMessages,
      acceptanceRate: metrics.networking.acceptanceRate,
      totalLeads,
      totalQueries,
      openQueries: metrics.support.totalOpenQueries,
    };
  }, [metrics]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
          Loading dashboard metrics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={fetchMetrics}
              startIcon={<RefreshIcon />}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!metrics) {
    return null;
  }

  const tabs = [
    { label: 'Overview', icon: <DashboardIcon /> },
    { label: 'Users', icon: <PeopleIcon /> },
    { label: 'Exhibitors', icon: <StoreIcon /> },
    { label: 'Meetings', icon: <EventIcon /> },
    { label: 'Leads', icon: <ContactsIcon /> },
    { label: 'Engagement', icon: <NetworkIcon /> },
    { label: 'Support', icon: <SupportIcon /> },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.875rem',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchMetrics}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderColor: '#e5e7eb',
              color: '#374151',
              '&:hover': {
                borderColor: '#d1d5db',
                bgcolor: '#f9fafb',
              },
            }}
          >
            Refresh
          </Button>
          {/* <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              py: 1,
            }}
          >
            Export Report
          </Button> */}
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Paper
        sx={{
          borderRadius: 2,
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b82f6',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: '#6b7280',
              minHeight: 56,
              '&.Mui-selected': {
                color: '#3b82f6',
              },
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              id={`dashboard-tab-${index}`}
              aria-controls={`dashboard-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Panels */}

      {/* Overview Tab */}
      <TabPanel value={activeTab} index={0}>
        {summaryStats && (
          <>
            {/* Primary Stats */}
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#374151',
                mb: 2,
              }}
            >
              Key Metrics
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <MetricCard
                  title="Active Users"
                  value={summaryStats.activeUsers}
                  icon={<PeopleIcon />}
                  color={CHART_COLORS.primary}
                  subtitle={`${summaryStats.newUsersThisWeek} new this week`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <MetricCard
                  title="Total Exhibitors"
                  value={summaryStats.totalExhibitors}
                  icon={<StoreIcon />}
                  color={CHART_COLORS.success}
                  subtitle={`${summaryStats.verifiedExhibitors} verified`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <MetricCard
                  title="Scheduled Meetings"
                  value={summaryStats.totalMeetings}
                  icon={<EventIcon />}
                  color={CHART_COLORS.purple}
                  subtitle={`${summaryStats.completionRate}% completion rate`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <MetricCard
                  title="Active Badges"
                  value={summaryStats.activeBadges}
                  icon={<QrCodeIcon />}
                  color={CHART_COLORS.warning}
                  subtitle={`${summaryStats.totalQrScans} total scans`}
                />
              </Grid>
            </Grid>

            {/* Engagement Stats */}
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#374151',
                mb: 2,
              }}
            >
              Engagement Overview
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <MetricCard
                  title="Total Connections"
                  value={summaryStats.totalConnections}
                  icon={<NetworkIcon />}
                  color={CHART_COLORS.cyan}
                  subtitle={`${summaryStats.acceptanceRate}% acceptance rate`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <MetricCard
                  title="Conversations"
                  value={summaryStats.totalConversations}
                  icon={<ContactsIcon />}
                  color={CHART_COLORS.pink}
                  subtitle={`${summaryStats.totalMessages} messages`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <MetricCard
                  title="Total Leads"
                  value={summaryStats.totalLeads}
                  icon={<ContactsIcon />}
                  color={CHART_COLORS.lime}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <MetricCard
                  title="Open Support Queries"
                  value={summaryStats.openQueries}
                  icon={<SupportIcon />}
                  color={
                    summaryStats.openQueries > 0
                      ? CHART_COLORS.error
                      : CHART_COLORS.success
                  }
                  subtitle={`${summaryStats.totalQueries} total queries`}
                />
              </Grid>
            </Grid>

            {/* Quick Stats Grid */}
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#374151',
                mb: 2,
              }}
            >
              User Statistics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Inactive Users"
                  value={summaryStats.inactiveUsers}
                  icon={<PeopleIcon />}
                  color="#9ca3af"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="New This Month"
                  value={summaryStats.newUsersThisMonth}
                  icon={<PeopleIcon />}
                  color={CHART_COLORS.primary}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Featured Exhibitors"
                  value={summaryStats.featuredExhibitors}
                  icon={<StoreIcon />}
                  color={CHART_COLORS.purple}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="No-Show Rate"
                  value={summaryStats.noShowRate}
                  icon={<EventIcon />}
                  color={CHART_COLORS.error}
                  format="percent"
                />
              </Grid>
            </Grid>
          </>
        )}
      </TabPanel>

      {/* Users Tab */}
      <TabPanel value={activeTab} index={1}>
        <UserMetricsSection data={metrics.users} />
      </TabPanel>

      {/* Exhibitors Tab */}
      <TabPanel value={activeTab} index={2}>
        <ExhibitorMetricsSection data={metrics.exhibitors} />
      </TabPanel>

      {/* Meetings Tab */}
      <TabPanel value={activeTab} index={3}>
        <MeetingsMetricsSection data={metrics.meetings} />
      </TabPanel>

      {/* Leads Tab */}
      <TabPanel value={activeTab} index={4}>
        <LeadsMetricsSection data={metrics.leads} />
      </TabPanel>

      {/* Engagement Tab (QR Badges + Networking) */}
      <TabPanel value={activeTab} index={5}>
        <Box sx={{ mb: 4 }}>
          <QrBadgesMetricsSection data={metrics.qrBadges} />
        </Box>
        <NetworkingMetricsSection data={metrics.networking} />
      </TabPanel>

      {/* Support Tab (Support + Favorites) */}
      <TabPanel value={activeTab} index={6}>
        <Box sx={{ mb: 4 }}>
          <SupportMetricsSection data={metrics.support} />
        </Box>
        <FavoritesMetricsSection data={metrics.favorites} />
      </TabPanel>
    </Box>
  );
};

export default Dashboard;
